<script setup lang="ts">
const { locale } = useI18n()

const search = ref('')

const querySearch = useRouteQuery<string>('s', '')

const debouncedUpdateSearch = useDebounceFn(() => {
  if (search.value.length > 1) {
    refreshProductLines()
    refreshProductSets()
  }
}, 300)

watch(search, () => {
  querySearch.value = search.value
  debouncedUpdateSearch()
})

search.value = querySearch.value

const searchRepository = createSearchRepository()

const {
  data: productLines,
  status: productLinesStatus,
  refresh: refreshProductLines,
} = await useLazyAsyncData(
  () => searchRepository.searchProductLines(search.value),
  {
    watch: [locale],
    immediate: querySearch.value !== '',
  }
)

const {
  data: productSets,
  status: productSetsStatus,
  refresh: refreshProductSets,
} = await useLazyAsyncData(
  () => searchRepository.searchProductSets(search.value),
  {
    watch: [locale],
    immediate: querySearch.value !== '',
  }
)

const noProductLines = computed(() => {
  return (productLines.value?.items ?? []).length === 0
})

const noProductSets = computed(() => {
  return (productSets.value?.items ?? []).length === 0
})

const noSearchResults = computed(() => {
  return noProductLines.value && noProductSets.value

  // TODO add references and fulltext check
})
</script>

<template>
  <div>
    <div class="container py-10">
      <form @submit.prevent>
        <div class="mb-8 w-full">
          <div class="relative flex duration-700">
            <div class="flex w-full flex-col">
              <UInput
                id="search-text"
                v-model="search"
                :ui="{
                  width: 'w-full left-0 !-ml-[8px] rounded-0 py-2',
                  shadow: '',
                  ring: '',
                  rounded: '',
                }"
                color="primary"
                class="h-8 w-full border-black"
                size="lg"
                :placeholder="$t('base.search.placeholder')"
              />
            </div>
          </div>
        </div>
      </form>
    </div>
    <div class="bg-neutral-100">
      <div class="container py-10">
        <UiSkeletonProductCard
          v-if="
            productLinesStatus === 'pending' || productSetsStatus === 'pending'
          "
        />
        <div
          v-else-if="search.length < 2"
          class="font-pragmatica-ext py-60 text-center text-2xl font-bold"
        >
          {{ $t('base.search.minimalLength') }}
        </div>
        <div v-else-if="noSearchResults" class="py-60 text-center">
          <h2 class="font-pragmatica-ext text-2xl font-bold">
            {{ $t('base.search.noResults') }}
          </h2>
        </div>
        <template v-else>
          <SearchProductLines
            v-if="!noProductLines && productLines"
            :product-lines="productLines"
          />
          <SearchProductSets
            v-if="!noProductSets && productSets"
            :product-sets="productSets"
          />
          <SearchReferences v-if="false" />
          <SearchFulltext v-if="false" />
        </template>
      </div>
    </div>
  </div>
</template>
